// src/components/documents/SavedLocationPicker.js
import React, { useState, useRef, useEffect } from 'react';
import { useApp } from '../../contexts/AppContext';

/**
 * Sits next to an Origin/Destination field. Two things:
 *  - A 📌 button opens a dropdown of previously-saved locations to pick
 *    from instantly (no searching or map-clicking needed again)
 *  - A 💾 button (only enabled once the field has real coordinates) saves
 *    the CURRENT value under a friendly name for reuse next time — this is
 *    the fix for addresses the free geocoding service will never find on
 *    its own, like small subdivision streets: pin it once with a map
 *    click, save it, then just pick it from the list forever after.
 */
export default function SavedLocationPicker({ currentValue, onPick }) {
  const { savedLocations, addSavedLocation, deleteSavedLocation } = useApp();
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newName, setNewName] = useState('');
  const wrapRef = useRef(null);

  useEffect(() => {
    function onClickOutside(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) { setOpen(false); setSaving(false); }
    }
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const canSave = currentValue?.lat != null && currentValue?.lng != null;

  const handleSaveSubmit = async () => {
    if (!newName.trim() || !canSave) return;
    await addSavedLocation({
      name: newName.trim(),
      address: currentValue.address,
      lat: currentValue.lat,
      lng: currentValue.lng,
    });
    setNewName('');
    setSaving(false);
    setOpen(false);
  };

  return (
    <div ref={wrapRef} style={{ position: 'relative', display: 'flex', gap: 6, marginTop: 6 }}>
      <button
        type="button"
        onClick={() => { setOpen(o => !o); setSaving(false); }}
        style={{
          fontSize: 11, fontWeight: 600, padding: '5px 10px', borderRadius: 7, cursor: 'pointer',
          background: 'var(--bgInput)', border: '1px solid var(--borderInput)', color: 'var(--textMuted)',
          display: 'flex', alignItems: 'center', gap: 5,
        }}
      >
        📌 Saved{savedLocations.length > 0 ? ` (${savedLocations.length})` : ''}
      </button>
      <button
        type="button"
        onClick={() => { if (canSave) { setSaving(true); setOpen(true); setNewName(currentValue.address?.split(',')[0] || ''); } }}
        disabled={!canSave}
        title={canSave ? 'Save this location for future use' : 'Set a location first'}
        style={{
          fontSize: 11, fontWeight: 600, padding: '5px 10px', borderRadius: 7,
          cursor: canSave ? 'pointer' : 'not-allowed', opacity: canSave ? 1 : 0.4,
          background: 'var(--bgInput)', border: '1px solid var(--borderInput)', color: 'var(--textMuted)',
        }}
      >
        💾 Save
      </button>

      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, marginTop: 4, zIndex: 9999, width: 280,
          background: 'var(--bgModal)', border: '1px solid var(--borderInput)', borderRadius: 10,
          boxShadow: 'var(--shadow)', padding: 10,
        }}>
          {saving ? (
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--textMuted)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Save as</div>
              <input
                autoFocus
                value={newName}
                onChange={e => setNewName(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleSaveSubmit()}
                placeholder="e.g. Felville Subdivision Warehouse"
                style={{
                  width: '100%', background: 'var(--bgInput)', border: '1.5px solid var(--borderInput)',
                  borderRadius: 7, padding: '7px 9px', color: 'var(--textPrimary)', fontSize: 12, outline: 'none',
                  fontFamily: 'DM Sans, sans-serif', marginBottom: 8,
                }}
              />
              <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                <button type="button" onClick={() => setSaving(false)} style={{ fontSize: 11, padding: '5px 10px', borderRadius: 6, background: 'var(--bgHover)', border: '1px solid var(--border)', color: 'var(--textMuted)', cursor: 'pointer' }}>Cancel</button>
                <button type="button" onClick={handleSaveSubmit} disabled={!newName.trim()} style={{ fontSize: 11, fontWeight: 700, padding: '5px 10px', borderRadius: 6, background: '#f97316', border: 'none', color: '#fff', cursor: newName.trim() ? 'pointer' : 'not-allowed', opacity: newName.trim() ? 1 : 0.5 }}>Save</button>
              </div>
            </div>
          ) : (
            <div style={{ maxHeight: 260, overflowY: 'auto' }}>
              {savedLocations.length === 0 ? (
                <div style={{ fontSize: 11, color: 'var(--textFaint)', padding: '8px 4px' }}>
                  No saved locations yet. Pin a spot on the map, then click 💾 Save to add it here for next time.
                </div>
              ) : (
                savedLocations.map(loc => (
                  <div
                    key={loc.id}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 6, padding: '7px 6px', borderRadius: 7,
                      cursor: 'pointer',
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--bgHover)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    <div
                      style={{ flex: 1, minWidth: 0 }}
                      onClick={() => { onPick(loc); setOpen(false); }}
                    >
                      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--textPrimary)' }}>{loc.name}</div>
                      {loc.address && <div style={{ fontSize: 10, color: 'var(--textFaint)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{loc.address}</div>}
                    </div>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); deleteSavedLocation(loc.id); }}
                      title="Remove"
                      style={{ background: 'none', border: 'none', color: 'var(--textFaint)', cursor: 'pointer', fontSize: 12, flexShrink: 0, padding: 2 }}
                    >
                      ✕
                    </button>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
